import React, { useEffect } from 'react';
import { useSessionPermissions } from '@/features/sessions/hooks/useSessionPermissions';
import { GroupSessionRole } from '@/features/sessions/types/group-session.types';
import { supabase } from '@/core/api/supabase';

interface RoleBasedStylesProps {
  sessionId?: string | null;
}

/**
 * Role-Based Styles Component
 * 
 * Adds CSS classes to the document body based on user role
 * Used to hide or show UI elements globally based on permissions
 */
export const RoleBasedStyles: React.FC<RoleBasedStylesProps> = ({ sessionId }) => {
  // Use undefined instead of null to match expected parameter type
  const { userRole, isOwner, isFacilitator } = useSessionPermissions(sessionId || undefined);
  
  useEffect(() => {
    // Function to check direct facilitator status
    async function checkFacilitatorStatus() {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;
        
        const { data } = await supabase
          .from('profiles')
          .select('role')
          .eq('id', user.id)
          .single();
          
        if (data?.role === 'facilitator') {
          // Add facilitator class to body
          document.body.classList.add('ww-facilitator');
        } else {
          document.body.classList.remove('ww-facilitator');
        }
      } catch (err) {
        console.error('Error checking facilitator status:', err);
      }
    }
    
    // Run facilitator check
    checkFacilitatorStatus();
    
    // Add/remove CSS classes based on user role
    if (userRole === GroupSessionRole.PARTICIPANT) {
      document.body.classList.add('ww-participant');
      document.body.classList.remove('ww-editor', 'ww-owner');
    } else if (isOwner) {
      document.body.classList.add('ww-owner');
      document.body.classList.remove('ww-participant', 'ww-editor');
    } else if (isFacilitator) {
      document.body.classList.add('ww-facilitator');
      document.body.classList.remove('ww-participant', 'ww-editor');
    } else {
      document.body.classList.add('ww-editor');
      document.body.classList.remove('ww-participant', 'ww-owner');
    }
    
    return () => {
      // Clean up classes when component unmounts
      document.body.classList.remove('ww-participant', 'ww-editor', 'ww-owner', 'ww-facilitator');
    };
  }, [userRole, isOwner, isFacilitator, sessionId]);
  
  // This component doesn't render anything visible
  return null;
};

export default RoleBasedStyles;
